import React from 'react';

const IconBuild = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.1-2.1 2.7-2.5z" />
  </svg>
);

const IconProtect = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-3z" />
    <path d="M9.5 12l2 2 3.5-4" />
  </svg>
);

const IconEvolve = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-2.6-6.4" />
    <path d="M21 3v6h-6" />
  </svg>
);

function Home({ openModal }) {
  return (
    <>
      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-glow" aria-hidden="true" />
        <div className="container hero-grid">
          <div className="hero-content">
            <span className="eyebrow">Smart contract security &amp; engineering</span>
            <h1 className="hero-title">
              Security‑first smart contract audits and development for teams who{' '}
              <span className="accent">can&apos;t afford a hack</span>.
            </h1>
            <p className="hero-subtitle">
              Every ProofSec engagement follows a single, structured methodology: consistent depth,
              validated findings, and a report you can act on. Independent smart contract audits
              for DeFi, NFT, and Web3 — fast and at a fraction of the cost of big firms.
            </p>
            <div className="hero-actions">
              <button className="cta-button hero-cta" onClick={openModal}>Request an Audit</button>
              <a href="#sample" className="cta-button secondary">View a Sample Report</a>
            </div>
          </div>

          <div className="hero-panel" aria-hidden="true">
            <div className="terminal">
              <div className="terminal-bar">
                <span className="terminal-dot" />
                <span className="terminal-dot" />
                <span className="terminal-dot" />
                <span className="terminal-title">proofsec — audit session</span>
              </div>
              <div className="terminal-body">
                <p><span className="t-prompt">$</span> proofsec audit ./contracts --full</p>
                <p><span className="t-ok">✓</span> 14 contracts mapped, entry points indexed</p>
                <p><span className="t-ok">✓</span> threat model built — 6 trust boundaries</p>
                <p><span className="t-warn">▲ HIGH</span> reentrancy in <span className="t-code">withdraw()</span></p>
                <p className="t-indent">└ PoC verified · remediation drafted</p>
                <p><span className="t-ok">✓</span> invariants 18/18 holding post‑fix</p>
                <p><span className="t-ok">✓</span> fuzzing pass — 1.2M inputs, 0 violations</p>
                <p><span className="t-prompt">$</span> report ready <span className="t-code">proofsec-audit-final.pdf</span><span className="t-cursor" /></p>
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-value">10+ yrs</span>
              <span className="stat-label">Smart contract engineering experience</span>
            </div>
            <div className="stat">
              <span className="stat-value">PoC‑verified</span>
              <span className="stat-label">Critical &amp; high findings come with exploit code</span>
            </div>
            <div className="stat">
              <span className="stat-value">3–5 days</span>
              <span className="stat-label">Fastest audit turnaround</span>
            </div>
            <div className="stat">
              <span className="stat-value">From $750</span>
              <span className="stat-label">A fraction of big‑firm pricing</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why Security Matters */}
      <section className="security-matters">
        <div className="container">
          <span className="eyebrow">01 — Why it matters</span>
          <h2 className="section-title">Billions lost. Don&apos;t be next.</h2>
          <p className="section-text">
            Billions of dollars have been lost to hacks, exploits, and unchecked vulnerabilities.
            Security isn&apos;t optional — it&apos;s mission-critical. At ProofSec, we provide professional,
            independent audits that give you proof, not promises.
          </p>
          <div className="pillars">
            <div className="pillar-card">
              <div className="pillar-icon"><IconBuild /></div>
              <h3 className="pillar-title">Build</h3>
              <p className="pillar-desc">
                Designing and developing secure, scalable applications and systems from the ground up.
              </p>
            </div>
            <div className="pillar-card">
              <div className="pillar-icon"><IconProtect /></div>
              <h3 className="pillar-title">Protect</h3>
              <p className="pillar-desc">
                Identifying vulnerabilities through in-depth security audits and real-world attack simulations.
              </p>
            </div>
            <div className="pillar-card">
              <div className="pillar-icon"><IconEvolve /></div>
              <h3 className="pillar-title">Evolve</h3>
              <p className="pillar-desc">
                Continuously monitoring, improving, and strengthening systems to adapt to emerging threats and ensure long-term resilience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section id="services" className="services">
        <div className="container">
          <span className="eyebrow">02 — Audit packages</span>
          <h2 className="section-title">Audit Packages</h2>
          <p className="section-subtitle">Choose the package that fits your project&apos;s needs.</p>
          <div className="services-grid">
            <div className="service-card">
              <h3 className="service-title">Quick Review</h3>
              <div className="service-price"><span className="price-from">From</span> $750</div>
              <ul className="service-features">
                <li>Up to 500 lines of code</li>
                <li>3–5 day turnaround</li>
                <li>PDF report &amp; badge</li>
              </ul>
              <button className="cta-button card-cta secondary" onClick={openModal}>Get Started</button>
            </div>
            <div className="service-card featured">
              <span className="featured-tag">Most Popular</span>
              <h3 className="service-title">Standard Audit</h3>
              <div className="service-price"><span className="price-from">From</span> $2,100</div>
              <ul className="service-features">
                <li>Full contract suite review</li>
                <li>1–2 weeks</li>
                <li>Manual + automated analysis</li>
              </ul>
              <button className="cta-button card-cta" onClick={openModal}>Get Started</button>
            </div>
            <div className="service-card">
              <h3 className="service-title">Comprehensive Audit</h3>
              <div className="service-price"><span className="price-from">From</span> $4,900</div>
              <ul className="service-features">
                <li>Complex protocols (DEX, bridge, lending)</li>
                <li>2–3 weeks</li>
                <li>Gas optimization + exploit testing</li>
              </ul>
              <button className="cta-button card-cta secondary" onClick={openModal}>Get Started</button>
            </div>
          </div>
        </div>
      </section>

      {/* Development Services */}
      <section id="development" className="development-services">
        <div className="container">
          <span className="eyebrow">03 — Development</span>
          <h2 className="section-title">Custom Development Services</h2>
          <p className="section-subtitle">From smart contracts to full Web3 applications — we build it all.</p>
          <div className="services-grid">
            <div className="service-card">
              <h3 className="service-title">Smart Contract Development</h3>
              <div className="service-price"><span className="price-from">From</span> $3,000</div>
              <ul className="service-features">
                <li>Custom smart contract creation</li>
                <li>ERC-20, ERC-721, ERC-1155 tokens</li>
                <li>DeFi protocols &amp; DEX contracts</li>
                <li>Gas optimization included</li>
                <li>Full documentation &amp; testing</li>
              </ul>
              <button className="cta-button card-cta secondary" onClick={openModal}>Discuss Your Project</button>
            </div>
            <div className="service-card featured">
              <span className="featured-tag">Most Popular</span>
              <h3 className="service-title">Full Web3 Application</h3>
              <div className="service-price"><span className="price-from">From</span> $9,000</div>
              <ul className="service-features">
                <li>Complete dApp development</li>
                <li>Frontend + smart contracts</li>
                <li>Wallet integration (MetaMask, etc.)</li>
                <li>Deployment &amp; testing</li>
                <li>3 months post-launch support</li>
              </ul>
              <button className="cta-button card-cta" onClick={openModal}>Discuss Your Project</button>
            </div>
            <div className="service-card">
              <h3 className="service-title">Enterprise Web3 Solution</h3>
              <div className="service-price"><span className="price-from">From</span> $21,000</div>
              <ul className="service-features">
                <li>Complex multi-contract systems</li>
                <li>Cross-chain integration</li>
                <li>Advanced security features</li>
                <li>Custom blockchain solutions</li>
                <li>6 months support &amp; maintenance</li>
              </ul>
              <button className="cta-button card-cta secondary" onClick={openModal}>Discuss Your Project</button>
            </div>
          </div>
          <div className="development-note">
            <p>All development projects include a complimentary security audit upon completion.</p>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section id="process" className="process">
        <div className="container">
          <span className="eyebrow">04 — Process</span>
          <h2 className="section-title">How It Works</h2>
          <p className="section-text">
            Our process combines automated analysis, manual review, and exploit pattern checks to deliver actionable results.
          </p>
          <div className="process-timeline">
            <div className="process-step">
              <span className="process-index">01</span>
              <h3 className="process-step-title">Submit Code</h3>
              <p className="process-step-desc">Share your repository or contract files with agreed scope.</p>
            </div>
            <div className="process-step">
              <span className="process-index">02</span>
              <h3 className="process-step-title">Audit &amp; Review</h3>
              <p className="process-step-desc">Manual review, automated analysis, and exploit testing.</p>
            </div>
            <div className="process-step">
              <span className="process-index">03</span>
              <h3 className="process-step-title">Fix Recommendations</h3>
              <p className="process-step-desc">Prioritised findings with concrete remediation guidance.</p>
            </div>
            <div className="process-step">
              <span className="process-index">04</span>
              <h3 className="process-step-title">Final Report + Badge</h3>
              <p className="process-step-desc">A report you can publish, plus your ProofSec audit badge.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section id="methodology" className="methodology">
        <div className="container">
          <span className="eyebrow">05 — Methodology</span>
          <h2 className="section-title">How we audit</h2>
          <p className="methodology-intro">
            Every ProofSec engagement follows a single, structured methodology so you get consistent depth and a report you can act on.
          </p>

          <div className="methodology-layout">
            <div className="methodology-main">
              <div className="methodology-steps">
                <div className="methodology-step">
                  <div className="step-number">01</div>
                  <div className="step-content">
                    <h3 className="step-title">Codebase and architecture analysis</h3>
                    <p className="step-description">
                      We map your contracts, dependencies, and data flows. We identify entry points, state changes, and external interactions so nothing critical is out of scope.
                    </p>
                  </div>
                </div>
                <div className="methodology-step">
                  <div className="step-number">02</div>
                  <div className="step-content">
                    <h3 className="step-title">Protocol and threat modelling</h3>
                    <p className="step-description">
                      We build a model of how your system behaves: who can call what, how value and state move, and where trust boundaries sit. That model drives which attack classes we prioritise.
                    </p>
                  </div>
                </div>
                <div className="methodology-step">
                  <div className="step-number">03</div>
                  <div className="step-content">
                    <h3 className="step-title">Invariant and property definition</h3>
                    <p className="step-description">
                      We define what must hold in your protocol (e.g. balance conservation, access rules, accounting invariants). Those properties become the criteria we test against.
                    </p>
                  </div>
                </div>
                <div className="methodology-step">
                  <div className="step-number">04</div>
                  <div className="step-content">
                    <h3 className="step-title">Attack design and sequencing</h3>
                    <p className="step-description">
                      We design multi-step attack scenarios—reentrancy, flash loans, oracle abuse, access-control bypass, economic edge cases—tailored to your design, not generic checklists.
                    </p>
                  </div>
                </div>
                <div className="methodology-step">
                  <div className="step-number">05</div>
                  <div className="step-content">
                    <h3 className="step-title">Exploit verification</h3>
                    <p className="step-description">
                      Where we identify a viable attack, we verify it with executable proof-of-concept code. Findings we report as high or critical are validated, not just theorised.
                    </p>
                  </div>
                </div>
                <div className="methodology-step">
                  <div className="step-number">06</div>
                  <div className="step-content">
                    <h3 className="step-title">Mutation and edge-case testing</h3>
                    <p className="step-description">
                      We vary parameters, call order, and roles to stress the same attack class. That catches “almost fixed” or boundary conditions a single test would miss.
                    </p>
                  </div>
                </div>
                <div className="methodology-step">
                  <div className="step-number">07</div>
                  <div className="step-content">
                    <h3 className="step-title">Fuzzing and input-space coverage</h3>
                    <p className="step-description">
                      We systematically exercise public interfaces with large input sets to trigger unexpected reverts, invariant violations, or accounting bugs.
                    </p>
                  </div>
                </div>
                <div className="methodology-step">
                  <div className="step-number">08</div>
                  <div className="step-content">
                    <h3 className="step-title">Token and economic behaviour</h3>
                    <p className="step-description">
                      We account for non-standard token behaviour (fee-on-transfer, rebasing, hooks) and economic assumptions (slippage, MEV, flash-loan cost). Your report reflects real-world conditions, not only “ideal” tokens.
                    </p>
                  </div>
                </div>
                <div className="methodology-step">
                  <div className="step-number">09</div>
                  <div className="step-content">
                    <h3 className="step-title">Economic impact assessment</h3>
                    <p className="step-description">
                      For issues that could extract or destroy value, we estimate feasibility: gas, fees, and profitability. That helps you prioritise by risk, not only by severity label.
                    </p>
                  </div>
                </div>
                <div className="methodology-step">
                  <div className="step-number">10</div>
                  <div className="step-content">
                    <h3 className="step-title">Report and remediation</h3>
                    <p className="step-description">
                      You receive a clear, prioritised report: executive summary, scope, findings with root cause and impact, proof-of-concept where relevant, and concrete remediation steps. We’re available to clarify and to review fixes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="methodology-summary">
                <p>
                  ProofSec audits follow a fixed pipeline: we analyse your codebase and architecture, model the protocol and its threats, define invariants and security properties, design and sequence attacks, and verify them with proof-of-concept code. We then stress-test with mutation and fuzzing, account for token and economic behaviour, assess economic impact, and deliver a prioritised report with root causes and remediation. The result is a repeatable, thorough review that gives you confidence and clear next steps.
                </p>
              </div>

              <div className="deliverables">
                <h3 className="deliverables-title">What you get</h3>
                <ul className="deliverables-list">
                  <li>Written report (PDF and structured export)</li>
                  <li>Findings by severity with root cause and impact</li>
                  <li>Proof-of-concept code for critical/high findings where applicable</li>
                  <li>Concrete remediation recommendations</li>
                  <li>Optional: scope and methodology summary, and a call to walk through results and fixes</li>
                </ul>
              </div>
            </div>

            <aside className="why-proofsec">
              <h3 className="why-proofsec-title">Why ProofSec</h3>
              <ul className="why-proofsec-list">
                <li><strong>Structured, not ad hoc</strong> — Same methodology on every audit so coverage and quality are predictable.</li>
                <li><strong>Verified, not theoretical</strong> — Critical and high-severity findings are backed by runnable proof-of-concept where applicable.</li>
                <li><strong>Actionable</strong> — Every finding comes with root cause and remediation guidance you can implement.</li>
                <li><strong>Protocol-aware</strong> — Review is tailored to your design, token types, and economic model, not a generic scan.</li>
                <li><strong>Transparent scope</strong> — We agree what’s in and out of scope and state limitations so you know exactly what was reviewed.</li>
              </ul>
            </aside>
          </div>
        </div>
      </section>

      {/* AI and Auditing */}
      <section className="ai-audit">
        <div className="container">
          <span className="eyebrow">06 — Human + AI</span>
          <h2 className="section-title">Can AI replace auditing and development?</h2>
          <p className="section-text">
            AI is a powerful accelerator for experienced security engineers — it can surface patterns, generate test cases, and speed up code review.
            But on its own, it doesn&apos;t understand your protocol, your risk tolerance, or the real-world incentives around your project.
          </p>
          <div className="ai-layout">
            <div className="ai-column">
              <h3 className="ai-subtitle">Where AI helps</h3>
              <ul className="ai-list ai-list-pro">
                <li>Quickly exploring large codebases and dependency graphs.</li>
                <li>Generating edge-case tests, fuzz inputs, and mutation scenarios.</li>
                <li>Spotting known vulnerability patterns and suspicious code paths.</li>
                <li>Automating parts of documentation and report drafting.</li>
              </ul>
            </div>
            <div className="ai-column">
              <h3 className="ai-subtitle">Where AI falls short</h3>
              <ul className="ai-list ai-list-con">
                <li>It can miss protocol-specific economic attacks and governance edge cases.</li>
                <li>It can hallucinate fixes or misunderstand business logic and threat models.</li>
                <li>It can&apos;t take responsibility for risk decisions or stand behind an audit.</li>
              </ul>
            </div>
          </div>
          <p className="ai-highlight">
            At ProofSec, we use in-house, custom AI tooling to support — not replace — our auditors and developers.
            Every finding, exploit, and remediation plan is designed, reviewed, and owned by human experts with deep Web3 experience.
          </p>
        </div>
      </section>

      {/* Sample Report */}
      <section id="sample" className="sample-report">
        <div className="container sample-layout">
          <div className="sample-copy">
            <span className="eyebrow">07 — Sample report</span>
            <h2 className="section-title">See How We Work</h2>
            <p className="section-text">
              Download a redacted sample audit report to understand our methodology and deliverables.
            </p>
            <button className="cta-button" onClick={openModal}>Request the Full Sample</button>
          </div>
          <div className="sample-frame">
            <img src="./ProofsecAuditExample.png" alt="Sample ProofSec audit report" className="sample-report-image" />
          </div>
        </div>
      </section>

      {/* About ProofSec */}
      <section className="about">
        <div className="container">
          <span className="eyebrow">08 — About</span>
          <h2 className="section-title">Who We Are</h2>
          <p className="section-text">
            ProofSec was founded by blockchain engineers with over 10 years of smart contract development and security experience.
            We&apos;ve built and reviewed DEXs, token systems, staking platforms, and cross-chain bridges. Our mission is to deliver
            high-quality audits that are fast, transparent, and affordable for projects of all sizes.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <div className="final-cta-panel">
            <h2 className="final-cta-title">Launch Secure. Launch with ProofSec.</h2>
            <p className="final-cta-sub">Tell us about your project and get a scoped quote within 24 hours.</p>
            <button className="cta-button large" onClick={openModal}>Request Your Project Today</button>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
